import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Popconfirm,
  Typography,
  Form,
  InputNumber,
  notification,
  Card,
  Space,
  Alert,
  Col,
  Row,
  Modal,
  Tooltip,
} from 'antd';

import BreadcrumbContainer from '../../containers/Breadcrumb';
import CustomButton from '../../components/CustomButton';
import INTERNAL_LINKS from '../../enums/InternalLinks';
import MESSAGES from '../../enums/Messages';
import {
  post,
  put,
  remove,
  ajaxTable as filterAjaxTable,
  renumerate,
  swap,
  info as getTermInfo,
  duplicate,
  editText,
} from '../../api/module/term_products';
import AjaxTable from '../../components/AjaxTable';

import './style.scss';
import { catchException } from '../../utils/helpers/helpers';
import ProductFields, { ProductTextOnlyFields } from './fields';
import {
  CopyFilled,
  DeleteFilled,
  EditFilled,
  LoadingOutlined,
  SettingOutlined,
  SwapLeftOutlined,
  SwapOutlined,
  SwapRightOutlined,
} from '@ant-design/icons';
import IconColors from '../../enums/IconColors';

const { Title, Paragraph } = Typography;

const DuplicateModal = ({ record, onClose }) => {
  const [dForm] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      await duplicate(record.id, values);
      setLoading(false);
      onClose(true);
    } catch (error) {
      setLoading(false);
      catchException(error);
    }
  };

  return (
    <Modal
      title="Duplicar producto"
      closable={!loading}
      footer={
        <CustomButton
          type="default"
          text="Duplicar"
          htmlType="button"
          loading={loading}
          onClick={() => dForm.submit()}
        />
      }
      onCancel={() => onClose()}
      open={record?.id}
      destroyOnClose
    >
      <Form
        form={dForm}
        onFinish={onFinish}
        layout="vertical"
        initialValues={{ n: 0 }}
      >
        <Form.Item
          name="n"
          label="¿Cuántas veces quiere duplicar este producto?"
          rules={[
            { required: true, message: 'Indique un número' },
            {
              validator: (_, value) => {
                if (!value) {
                  return Promise.resolve();
                } else if (isNaN(value)) {
                  return Promise.reject(
                    new Error('Debe ser un valor numérico'),
                  );
                }
                if (value < 1) {
                  return Promise.reject(new Error('Debe ser positivo'));
                }
                if (value > 50) {
                  return Promise.reject(
                    new Error('El número es demasiado alto'),
                  );
                }
                if (value.toString().includes('.')) {
                  return Promise.reject(new Error('Debe ser un valor entero'));
                }

                return Promise.resolve();
              },
            },
          ]}
        >
          <InputNumber />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const ProductsList = ({
  handleEdit,
  handleDuplicate,
  deleteDataSource,
  termID,
  reload,
  onSwap,
  swapID,
  lSwap,
}) => {
  const [deleteID, setDeleteID] = useState();

  const columns = [
    {
      title: 'No.',
      dataIndex: 'codigo',
      width: '3rem',
      align: 'center',
    },
    {
      title: 'Productos y/o entregables',
      dataIndex: 'producto_nombre',
      width: '20rem',
    },
    {
      title: 'Tipo',
      dataIndex: 'es_apoyo',
      render: (t) => (t ? 'Gasto' : 'Producto'),
      align: 'center',
      width: '6rem',
    },
    {
      title: 'Estado Guardado',
      dataIndex: 'info_detallada',
      width: '6rem',
    },
    {
      title: 'Fijo o Variable',
      dataIndex: 'variable',
      render: (t) => (t ? 'Variable' : t === false ? 'Fijo' : ''),
      align: 'center',
      width: '6rem',
    },
    {
      title: 'Tiene topes',
      dataIndex: 'tiene_tope',
      render: (t) => (t ? 'Si' : t === false ? 'No' : ''),
      align: 'center',
      width: '4rem',
    },
    {
      title: 'Unidades máximas',
      dataIndex: 'maximo_unitario',
      align: 'center',
      width: '5rem',
    },
    {
      title: 'Unidad',
      dataIndex: 'nombre_unitario',
      width: '8rem',
    },
    {
      title: 'Valor unidad',
      dataIndex: 'valor_unitario',
      width: '8rem',
      render: (t) =>
        t ? `$ ${t.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}` : null,
    },
    {
      title: 'Valor (máximo) a pagar',
      dataIndex: 'valor_total',
      width: '8rem',
      render: (t) =>
        t ? `$ ${t.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}` : null,
    },
    {
      title: <SettingOutlined />,
      key: 'acciones',
      fixed: 'right',
      align: 'center',
      width: '4em',
      render: (_, record) => {
        return (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Tooltip title="Editar">
              <EditFilled
                style={{ color: IconColors.green }}
                onClick={() => handleEdit(record)}
              />
            </Tooltip>
            <Tooltip
              title={
                lSwap && (record.id === swapID || record.id === lSwap)
                  ? 'Cambiando'
                  : record.id === swapID
                  ? 'Deseleccionar'
                  : 'Intercambiar'
              }
            >
              <>
                {lSwap && (record.id === swapID || record.id === lSwap) ? (
                  <LoadingOutlined />
                ) : record.id === swapID ? (
                  <SwapLeftOutlined onClick={() => onSwap(record)} />
                ) : swapID ? (
                  <SwapRightOutlined onClick={() => onSwap(record)} />
                ) : (
                  <SwapOutlined onClick={() => onSwap(record)} />
                )}
              </>
            </Tooltip>
            <Tooltip title="Eliminar">
              <Popconfirm
                title={MESSAGES.DELETE_CONFIRM_MSG}
                onConfirm={async () => {
                  setDeleteID(record.id);
                  await deleteDataSource(record.id);
                  setDeleteID();
                }}
              >
                <DeleteFilled
                  style={{ color: IconColors.red }}
                  rotate={record.id === deleteID ? 45 : 0}
                />
              </Popconfirm>
            </Tooltip>
            <Tooltip title="Duplicar">
              <CopyFilled
                style={{ color: IconColors.green }}
                onClick={() => handleDuplicate(record)}
              />
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <AjaxTable
      bordered
      sticky={{
        offsetHeader: -40,
      }}
      columns={columns}
      searchInput
      tableTitle={'Listado de productos'}
      endpoint={filterAjaxTable}
      reloadButton
      reloadSource={reload}
      scroll={{ x: false }}
      query={'/?filter[termino]=' + termID}
      showSizeChanger
      pageSize={50}
    />
  );
};

const ProductForm = ({
  termID,
  initialValues,
  id,
  doReload,
  setValuesData,
  info,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const save = async (data) => {
    try {
      let response;
      if (!id) {
        const newData = {
          termino: termID,
          ...data,
        };
        newData['partial_save'] = !data['info_detallada'];
        setLoading(true);
        response = await post(newData);
        form.resetFields();
      } else {
        const newData = {
          termino: termID,
          ...data,
        };
        newData['partial_save'] = !data['info_detallada'];
        setLoading(true);
        response = await put(id, newData);
      }
      setValuesData((s) => ({
        ...s,
        valor_contrato: response.data.valor_contrato,
        valor_asignado: response.data.valor_asignado,
      }));
      setLoading(false);
      doReload();
      notification.success({
        message: MESSAGES.SUCCESS_MSG,
        description: MESSAGES.DESC_SUCCESS_MSG,
        duration: 2,
      });
    } catch (err) {
      console.log(err);
      catchException(err);
      setLoading(false);
    }
  };

  const saveText = async (data) => {
    try {
      setLoading(true);
      await editText(id, data);
      setLoading(false);
      doReload();
      notification.success({
        message: MESSAGES.SUCCESS_MSG,
        description: MESSAGES.DESC_SUCCESS_MSG,
        duration: 2,
      });
    } catch (err) {
      console.log(err);
      catchException(err);
      setLoading(false);
    }
  };

  useEffect(() => {
    form.resetFields();
  }, [initialValues]);

  const buttonItemLayout = {
    wrapperCol: {
      span: 10,
      offset: 11,
    },
  };

  if (initialValues?.has_payment)
    return (
      <Form
        name="control-ref"
        form={form}
        initialValues={initialValues}
        onFinish={(data) => saveText(data)}
        autoComplete="off"
      >
        <ProductTextOnlyFields variable={initialValues.variable} />
        <Form.Item {...buttonItemLayout}>
          <CustomButton loading={loading} text="Editar" />
        </Form.Item>
      </Form>
    );

  return (
    <Form
      name="control-ref"
      form={form}
      initialValues={initialValues}
      onFinish={(data) => save(data)}
      autoComplete="off"
    >
      <ProductFields
        onlyUnlimited={info.onlyUnlimited}
        currencyType={info.currencyType}
      />
      <Form.Item noStyle dependencies={['info_detallada']}>
        {({ getFieldValue }) => {
          const saveText = initialValues ? 'Editar' : 'Agregar';
          return (
            <Form.Item {...buttonItemLayout}>
              <CustomButton
                loading={loading}
                text={
                  getFieldValue('info_detallada') === false
                    ? `${saveText} Parcial`
                    : saveText
                }
              />
            </Form.Item>
          );
        }}
      </Form.Item>
    </Form>
  );
};

export default function ({ match }) {
  const [termID] = useState(parseInt(match.params.term_id));
  const [projectID] = useState(parseInt(match.params.project_id));
  const [contractID] = useState(parseInt(match.params.contract_id));
  const [data, setData] = useState(null);
  const [id, setId] = useState(0);

  const [info, setInfo] = useState();

  const [reloadTable, setReloadTable] = useState();

  const [lRenumerate, setLRenumerate] = useState(false);
  const [swapData, setSwapData] = useState(undefined);
  const [lSwap, setLSwap] = useState(undefined);
  const [duplicateData, setDuplicateData] = useState(undefined);

  const topRef = useRef();

  const breadcrumbItems = [
    {
      text: 'Proyectos',
      to: `${INTERNAL_LINKS.PROJECTS}`,
    },
    {
      text: 'Términos de referencia por proyecto',
      to: `${INTERNAL_LINKS.TERMS}/${projectID}`,
    },
    {
      text: 'Formulario de Términos de referencia',
      to: `${INTERNAL_LINKS.TERM}/${termID}/${projectID}/${contractID}`,
    },
    {
      text: 'Productos y/o entregables y gastos',
    },
  ];

  const getInfo = async () => {
    const response = await getTermInfo(termID);
    setInfo(response.data);
  };

  const doReload = () => {
    setData(undefined);
    setId(undefined);
    getInfo();
    setReloadTable({});
  };

  useEffect(() => {
    getInfo();
  }, []);

  const deleteDataSource = async (id) => {
    try {
      await remove(id);
      notification.success({
        message: MESSAGES.SUCCESS_MSG,
        description: MESSAGES.DESC_SUCCESS_DELETE_MSG,
        duration: 2,
      });
      if (id === swapData?.id) setSwapData(undefined);
      doReload();
    } catch (err) {
      console.log(err);
      notification.error({
        message: MESSAGES.ERROR_MSG,
        description:
          'Este producto/gasto ya se encuentra asociado a un pago. Por favor, desvincúlelo o elimine la forma de pago.',
        duration: 2,
      });
    }
  };

  const handleEdit = (item) => {
    setData({
      ...item,
      info_detallada: item.info_detallada === 'Completo',
    });
    setId(item.id);
    if (topRef.current) topRef.current.scrollIntoView();
  };

  const handleDuplicate = (item) => {
    setDuplicateData(item);
  };

  const handleDuplicateClose = (r) => {
    setDuplicateData();
    if (r) doReload();
  };

  const doRenumerate = async () => {
    try {
      setLRenumerate(true);
      await renumerate(termID);
      doReload();
      setLRenumerate(false);
      notification.success({
        message: MESSAGES.SUCCESS_MSG,
        description: MESSAGES.DESC_SUCCESS_MSG,
        duration: 2,
      });
    } catch (error) {
      catchException(error);
    }
  };

  const doSwap = async (a, b) => {
    try {
      setLSwap(b);
      await swap(a, b);
      notification.success({
        message: MESSAGES.SUCCESS_MSG,
        description: MESSAGES.DESC_SUCCESS_MSG,
        duration: 2,
      });
      setSwapData(undefined);
      setLSwap(undefined);
      doReload();
    } catch (error) {
      catchException(error);
    }
  };

  const onSwap = (row) => {
    if (swapData) {
      if (row.id === swapData.id) setSwapData(undefined);
      else doSwap(swapData.id, row.id);
    } else {
      setSwapData(row);
    }
  };

  return (
    <div className="terms-location-container" ref={topRef}>
      <BreadcrumbContainer items={breadcrumbItems} />
      <Title className={'text-uppercase'} level={2}>
        TÉRMINOS DE REFERENCIA
      </Title>
      <Title level={3}>PROYECTO: {info?.proyecto_nombre}</Title>
      <Paragraph>
        Los siguientes términos de referencia complementan el texto del contrato
        firmado entre las partes y están subordinados a dichos contratos.
      </Paragraph>
      <Card
        size={'small'}
        bordered={false}
        title={<Title level={3}>D. ASPECTOS JURÍDICOS Y ECONÓMICOS</Title>}
      >
        <Title level={4}>
          Productos - Gastos u otros items contractuales de pago
        </Title>
        <ProductForm
          initialValues={data}
          termID={termID}
          id={id}
          doReload={doReload}
          setValuesData={setInfo}
          info={{
            onlyUnlimited: info?.unicamente_sin_tope,
            currencyType: info?.moneda_sigla,
          }}
        />
        {!info?.unicamente_sin_tope && (
          <Alert
            message={
              <>
                <Row>
                  <Col flex="18rem">Valor total del contrato: </Col>
                  <Col flex="auto">
                    <b>
                      {info?.moneda_simbolo}{' '}
                      {(info?.valor_contrato || 0)
                        ?.toString()
                        .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}{' '}
                      {info?.moneda_descripcion}
                    </b>
                  </Col>
                </Row>
                <Row>
                  <Col flex="18rem">Total de valores asignados a pagar: </Col>
                  <Col flex="auto">
                    <b>
                      {info?.moneda_simbolo}{' '}
                      {(info?.valor_asignado || 0)
                        .toString()
                        .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}{' '}
                      {info?.moneda_descripcion}
                    </b>
                  </Col>
                </Row>
                <Row>
                  <Col flex="18rem">
                    Valor pendiente por asignar a productos:{' '}
                  </Col>
                  <Col flex="auto">
                    <b>
                      {info?.moneda_simbolo}{' '}
                      {(
                        (info?.valor_contrato || 0) -
                        (info?.valor_asignado || 0)
                      )
                        .toFixed(2)
                        .toString()
                        .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}{' '}
                      {info?.moneda_descripcion}
                    </b>
                  </Col>
                </Row>
              </>
            }
            type={
              (info?.valor_contrato || 0) - (info?.valor_asignado || 0) !== 0
                ? 'error'
                : 'info'
            }
          />
        )}

        <br />
        <CustomButton
          htmlType="button"
          text="Renumerar"
          onClick={doRenumerate}
          loading={lRenumerate}
        />
        {swapData && (
          <>
            <br />
            <br />
            <Alert
              message="Intercambio de código"
              description={
                <>
                  Se ha seleccionado para intercambio de código el item
                  <br />
                  <br />
                  Código {swapData.codigo}: {swapData.descripcion}
                </>
              }
              type={'info'}
              showIcon
            />
          </>
        )}
        <DuplicateModal record={duplicateData} onClose={handleDuplicateClose} />
        <ProductsList
          termID={termID}
          handleEdit={handleEdit}
          handleDuplicate={handleDuplicate}
          deleteDataSource={deleteDataSource}
          reload={reloadTable}
          onSwap={onSwap}
          swapID={swapData?.id}
          lSwap={lSwap}
        />
        <CustomButton
          type={'default'}
          className={'back-button'}
          htmlType="button"
          text={
            <Link
              to={`${INTERNAL_LINKS.TERM}/${termID}/${projectID}/${contractID}/?module=D`}
            >
              Volver
            </Link>
          }
        />
      </Card>
    </div>
  );
}
